import { Component, input, OnInit, output, signal } from '@angular/core';
import { Client } from '../../../api/types';
import { TextListComponent } from '../../components/text-list.component';
import {
  SynButtonComponent,
  SynCheckboxComponent,
  SynergyFormsModule,
  SynIconComponent,
  SynInputComponent, SynTextareaComponent,
} from '@synergy-design-system/angular';
import { AbstractControl, FormControl, FormGroup, FormsModule, NgForm } from '@angular/forms';
import { HelpTextComponent } from '../../components/help-text.component';

export type ClientTemplate = 'frontend' | 'service' | 'custom';

@Component({
  selector: 'app-client-form',
  template: `
    <form #form=ngForm class="flex-col gap-1 form-width" (submit)="onSubmit(form)">
      <app-help-text>
        <syn-input
          [(ngModel)]="client().clientId"
          [required]="true"
          [readonly]="!editable()"
          [minlength]="3"
          [maxlength]="100"
          label="Client ID"
          i18n-label="@@client-id"
          name="clientId"
          data-e2e="input-client-id"
        />
        <span helpText i18n="@@client-id-help">
          A technical ID to configure the authentication flow in your OIDC/OAuth library.
          We recommend to use a human readable name, e.g. asset-hub.
        </span>
      </app-help-text>
      <app-help-text>
        <syn-input
          [(ngModel)]="client().name"
          [required]="false"
          [readonly]="!editable()"
          [minlength]="3"
          [maxlength]="100"
          label="Client Name"
          i18n-label="@@client-name"
          name="name"
          data-e2e="input-client-name"
        />
        <span helpText i18n="@@client-name-help">
          Human readable name of the client.
          This name might be displayed to a user, e.g. if they want to see their current login sessions.
        </span>
      </app-help-text>
      <app-help-text>
        <syn-textarea
          [(ngModel)]="client().description"
          [required]="true"
          [readonly]="!editable()"
          [maxlength]="500"
          [spellcheck]="false"
          label="Description"
          i18n-label="@@description"
          name="description"
          data-e2e="input-description"
        />
        <span helpText i18n="@@client-description-help">
          Document the purpose of this client and its corresponding application for other client managers.
        </span>
      </app-help-text>
      @if (template() === 'custom') {
        <div class="flex-row gap-2 form-width">
          <div class="flex-col gap-2" style="max-width: 320px">
            <app-help-text>
              <syn-checkbox
                [(ngModel)]="client().publicClient"
                [disabled]="!editable()"
                i18n="@@public-client"
                name="publicClient"
              >Public Client
              </syn-checkbox>
              <span helpText i18n="@@public-client-help">
              If this option is enabled, the client is public and doesn't need to have a secret.
              This client can be used in a standalone frontend application,
              which cannot store a client secret securely in a backend.
            </span>
            </app-help-text>
            <app-help-text>
              <syn-checkbox
                [(ngModel)]="client().serviceAccountEnabled"
                [disabled]="!editable() || client().publicClient"
                i18n="@@service-account"
                name="serviceAccountEnabled"
              >Service Account
              </syn-checkbox>
              <span helpText i18n="@@service-account-help">
              Enables a service account user that can authenticate via the the client credentials flow.
              You can use the service account for service-to-service communication.
            </span>
            </app-help-text>
          </div>
          <div class="flex-col gap-2" style="max-width: 320px">
            <app-help-text>
              <syn-checkbox
                [(ngModel)]="client().enforcePkce"
                [disabled]="!editable()"
                i18n="@@enforce-pkce"
                name="enforcePkce"
              >Enforce PKCE
              </syn-checkbox>
              <span helpText i18n="@@enforce-pkce-help">
              This enforces PKCE during initial login,
              i.e. the request has to use the parameter 'code_challenge_method' with value 'S256'.
            </span>
            </app-help-text>
            @if (showFullscope()) {
              <app-help-text>
                <syn-checkbox
                  [(ngModel)]="client().fullScopeAllowed"
                  [disabled]="!editable()"
                  i18n="@@full-scope"
                  name="fullScopeAllowed"
                >Full Scope
                </syn-checkbox>
                <span helpText i18n="@@full-scope-help">
                  Full scope maps all roles from all other tokens into the user's token.
                  Try to avoid it by using explicit role mappers.
                </span>
              </app-help-text>
            }
            <app-help-text>
              <syn-checkbox
                [(ngModel)]="enforceMfa"
                [disabled]="!editable()"
                i18n="@@enforce-mfa"
                name="enforceMfa"
              >Enforce MFA
              </syn-checkbox>
              <span helpText i18n="@@enforce-mfa-help">
                If this option is enabled, all users of your application need multi-factor authentication (MFA)
                for every login.
                Keep in mind that users without MFA are required to setup an authenticator during the login flow.
              </span>
            </app-help-text>
          </div>
        </div>
      }
      @if (template() !== 'service') {
        <app-help-text>
          <syn-input
            [(ngModel)]="client().baseUrl"
            [required]="false"
            [readonly]="!editable()"
            label="Service URL"
            i18n-label="@@service-url"
            name="baseUrl"
          />
          <span helpText i18n="@@service-url-help">
            The main entry point to your application.
            Users may be redirected to this URL after registration.
            Furthermore, this URL may be displayed to users viewing their active sessions.
          </span>
        </app-help-text>
        @if (template() !== 'frontend') {
          @if (showRootUrl()) {
            <app-help-text>
              <syn-input
                [(ngModel)]="client().rootUrl"
                [required]="false"
                [readonly]="!editable()"
                label="URL Prefix"
                i18n-label="@@url-prefix"
                name="rootUrl"
              />
              <span helpText i18n="@@url-prefix-help">
              This prefix allows you to avoid repetition when defining redirect and origin URLs.
              It is prepended to all relative URLs.
            </span>
            </app-help-text>
          }
          <app-help-text>
            <app-text-list
              [(ngModel)]="client().redirectUris"
              [readonly]="!editable()"
              label="Valid Redirect URLs"
              i18n-label="@@valid-redirect-urls"
              addLabel="Add URL"
              i18n-addLabel="@@add-url"
              regexPattern="^http[s]?:\\/\\/(([a-z0-9.\\-]+(:(\\d*\\*?))?)|(\\[[a-f0-9:]+\\](:(\\d*))?))($|\\/.*)|^\\/.*|^about:blank$"
              name="redirectUris"
            />
            <span helpText i18n="@@valid-redirect-urls-help">
              Valid URI pattern a browser can redirect to after a successful login or logout.
              Simple wildcards are allowed i.e. 'https://cloud.sick.com/*' but no plain wildcards "*".
            </span>
          </app-help-text>
          <app-help-text>
            <app-text-list
              [(ngModel)]="client().webOrigins"
              [readonly]="!editable()"
              label="Web Origins"
              i18n-label="@@web-origins"
              addLabel="Add URL"
              i18n-addLabel="@@add-url"
              regexPattern="http[s]?:\\/\\/[^\\/]+"
              name="webOrigins"
            />
            <span helpText i18n="@@web-origins-help">
              Define allowed access control origins.
              If you do not define your origins, you might encounter CORS errors.
              Do not enter relative paths, just enter the host itself, e.g. https://example.sick.com.
            </span>
          </app-help-text>
        }
      }
      @if (editable()) {
        <syn-button
          variant="filled"
          type="submit"
          [disabled]="!canSave(form)"
          data-e2e="btn-submit-client"
        >
          <syn-icon [name]="submitIcon() + '_fill'" slot="prefix" />
          <span>{{ submitText() }}</span>
        </syn-button>
      }
    </form>
  `,
  styles: [
    `
    `,
  ],
  imports: [
    TextListComponent,
    SynCheckboxComponent,
    SynInputComponent,
    SynTextareaComponent,
    SynergyFormsModule,
    FormsModule,
    SynButtonComponent,
    SynIconComponent,
    HelpTextComponent,
  ],
})
export class ClientFormComponent implements OnInit {
  client = input.required<Client>();
  editable = input(true);
  template = input<ClientTemplate>('custom');
  submitIcon = input('');
  submitText = input('');
  showFullscope = signal(false);
  showRootUrl = signal(false);
  submitClient = output<Client>();

  enforceMfa = signal(false);

  ngOnInit(): void {
    this.showFullscope.set(this.client().fullScopeAllowed);
    this.showRootUrl.set(this.client().rootUrl !== '');

    const minAcr = this.client().minimumAcrValue ?? 1;
    this.enforceMfa.set(minAcr >= 2);
  }

  onSubmit(form: NgForm) {
    if (form.invalid) return;

    // this.client() is prefilled with emptyClient if a new client is created
    const client: Client = { ...this.client(), ...form.value };
    if (this.template() == 'frontend') {
      try {
        client.publicClient = true;
        client.enforcePkce = true;
        client.serviceAccountEnabled = false;

        const serviceUrl = new URL(client.baseUrl);
        client.webOrigins = [serviceUrl.protocol + '//' + serviceUrl.host];
        let redirectUri = serviceUrl.toString();
        if (redirectUri.endsWith('/')) {
          redirectUri += '*';
        } else {
          redirectUri += '/*';
        }
        client.redirectUris = [redirectUri];
      } catch (_ex) {
        // Ignore invalid URLs
      }
    } else if (this.template() === 'service') {
      client.publicClient = false;
      client.serviceAccountEnabled = true;
    }

    // @ts-expect-error Remove form value that is not part of the request body
    delete client['enforceMfa'];
    client.minimumAcrValue = this.enforceMfa() ? 2 : undefined;

    this.submitClient.emit(client);

    form.form.markAsPristine();
  }

  canSave(form: NgForm): boolean {
    // console.log('Dirty', form.dirty, 'invalid', form.invalid);
    // console.log('Errors', this.getFormErrors(form.control));
    return !!form.dirty && !form.invalid;
  }

  getFormErrors(form: AbstractControl | null) {
    if (form instanceof FormControl) {
      // Return FormControl errors or null
      return form.errors ?? null;
    }
    if (form instanceof FormGroup) {
      const groupErrors = form.errors;
      // Form group can contain errors itself, in that case add'em
      const formErrors = groupErrors ? { groupErrors } : {};
      Object.keys(form.controls).forEach(key => {
        // Recursive call of the FormGroup fields
        const error = this.getFormErrors(form.get(key));
        if (error !== null) {
          // Only add error if not null
          // @ts-expect-error Ignore type errors
          formErrors[key] = error;
        }
      });
      // Return FormGroup errors or null
      return Object.keys(formErrors).length > 0 ? formErrors : null;
    }
    return null;
  }

}
